import { contentFitnessDimensionForKey } from "contracts/report/content-fitness/lookups";
import {
  withLeadingQuestionMark,
  withoutLeadingQuestionMark,
} from "../../boundaries/query-string.parser";
import type { ContentFitnessOrigin } from "../../enums/scan";

type PageDetailOrigin =
  | { source: "pages"; returnQuery?: string }
  | {
      source: "content-fitness";
      dimensionKey: string;
      dimensionOrigin?: ContentFitnessOrigin;
    }
  | { source: "journeys" }
  | { source: "issue"; issueId: string; issueReturnQuery?: string };

type PageDetailReturnContext = {
  href: string;
  label: string;
  parentHref: string;
  parentLabel: string;
};

export function pageDetailHref(
  websiteId: string,
  pageId: string,
  origin: PageDetailOrigin,
): string {
  const params = new URLSearchParams({ from: origin.source });
  if (origin.source === "pages" && origin.returnQuery) {
    params.set("return", normalizedQuery(origin.returnQuery));
  }
  if (origin.source === "content-fitness") {
    params.set("dimension", origin.dimensionKey);
    if (origin.dimensionOrigin) {
      params.set("dimensionFrom", origin.dimensionOrigin);
    }
  }
  if (origin.source === "issue") {
    params.set("issue", origin.issueId);
    if (origin.issueReturnQuery) {
      params.set("origin", normalizedQuery(origin.issueReturnQuery));
    }
  }
  return `/w/${websiteId}/scan/pages/${pageId}?${params.toString()}`;
}

export function pageDetailReturnContext(
  websiteId: string,
  pageId: string,
  input: {
    source: string | null;
    returnQuery: string | null;
    dimensionKey: string | null;
    dimensionOrigin: string | null;
    issueId: string | null;
    issueReturnQuery: string | null;
  },
): PageDetailReturnContext {
  if (input.source === "content-fitness" && input.dimensionKey) {
    const dimension = contentFitnessDimensionForKey(input.dimensionKey);
    if (dimension) {
      const query =
        input.dimensionOrigin === "scorecard" ? "?from=scorecard" : "";
      const href = `/w/${websiteId}/scan/content-fitness/${dimension.key}${query}`;
      return {
        href,
        label: `Back to ${dimension.label}`,
        parentHref: href,
        parentLabel: dimension.label,
      };
    }
  }
  if (input.source === "journeys") {
    const href = `/w/${websiteId}/scan/pages?view=paths`;
    return {
      href,
      label: "Back to Current Paths",
      parentHref: href,
      parentLabel: "Content & Messaging Flows",
    };
  }
  if (input.source === "issue" && input.issueId) {
    const query = normalizedQuery(input.issueReturnQuery);
    const href = `/w/${websiteId}/scan/issues/${input.issueId}${query}`;
    return {
      href,
      label: "Back to issue",
      parentHref: href,
      parentLabel: "Issue detail",
    };
  }
  const href = pagesReturnHref(websiteId, input.returnQuery, pageId);
  return {
    href,
    label: "Back to Content & Messaging Flows",
    parentHref: `/w/${websiteId}/scan/pages`,
    parentLabel: "Content & Messaging Flows",
  };
}

/** Where an issue-detail view returns to, per its `from` origin — href and
 * label derived together so a new origin cannot update one without the other. */
export function issueDetailReturnContext(
  websiteId: string,
  issueId: string,
  input: {
    source: string | null;
    returnQuery: string | null;
    dimensionKey: string | null;
    dimensionOrigin: string | null;
    pageId: string | null;
  },
): { href: string; label: string; parentLabel: string } {
  switch (input.source) {
    case "summary":
      return {
        href: `/w/${websiteId}/scan?issue=${issueId}`,
        label: "Back to Summary",
        parentLabel: "Summary",
      };
    case "pages":
      return {
        href: `/w/${websiteId}/scan/pages${normalizedQuery(input.returnQuery)}`,
        label: "Back to Content & Messaging Flows",
        parentLabel: "Content & Messaging Flows",
      };
    case "page":
      return {
        href: input.pageId
          ? `/w/${websiteId}/scan/pages/${input.pageId}${normalizedQuery(input.returnQuery)}`
          : `/w/${websiteId}/scan/pages`,
        label: "Back to page detail",
        parentLabel: "Page detail",
      };
    case "content-fitness": {
      const dimension = input.dimensionKey
        ? contentFitnessDimensionForKey(input.dimensionKey)
        : undefined;
      return dimension
        ? {
            href: `/w/${websiteId}/scan/content-fitness/${dimension.key}${
              input.dimensionOrigin === "scorecard" ? "?from=scorecard" : ""
            }`,
            label: `Back to ${dimension.label}`,
            parentLabel: dimension.label,
          }
        : {
            href: `/w/${websiteId}/scan/issues`,
            label: "Back to Content Fitness",
            parentLabel: "Content Fitness",
          };
    }
    case "overview":
      return {
        href: `/w/${websiteId}/overview`,
        label: "Back to Overview",
        parentLabel: "Overview",
      };
    default:
      return {
        href: issuesReturnHref(websiteId, input.returnQuery, issueId),
        label: "Back to Issues",
        parentLabel: "Issues",
      };
  }
}

function issuesReturnHref(
  websiteId: string,
  returnQuery: string | null,
  issueId: string,
): string {
  const params = new URLSearchParams(
    returnQuery ? withoutLeadingQuestionMark(returnQuery) : "",
  );
  params.set("issue", params.get("issue") ?? issueId);
  const query = params.toString();
  return `/w/${websiteId}/scan/issues${query ? `?${query}` : ""}`;
}

export function issueDetailFromPageHref(
  websiteId: string,
  issueId: string,
  pageId: string,
  pageDetailQuery: string,
): string {
  const params = new URLSearchParams({ from: "page", page: pageId });
  if (pageDetailQuery) {
    params.set("return", normalizedQuery(pageDetailQuery));
  }
  return `/w/${websiteId}/scan/issues/${issueId}?${params.toString()}`;
}

function pagesReturnHref(
  websiteId: string,
  returnQuery: string | null,
  pageId: string,
): string {
  const params = new URLSearchParams(normalizedQuery(returnQuery));
  params.set("page", params.get("page") ?? pageId);
  const query = params.toString();
  return `/w/${websiteId}/scan/pages${query ? `?${query}` : ""}`;
}

function normalizedQuery(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  return withLeadingQuestionMark(value);
}
